import React, { useCallback, useRef, useState } from 'react';
import { UploadCloud } from 'react-feather';

/**
 * DropZone — the file picker for Data Import and the wizard's Upload step.
 * Both places had their own copy; this one actually handles the DROP (the
 * old Data Import zone said "or drag and drop" but bound no drop handler).
 *
 * props
 *   accept    input accept string
 *   multiple  bool
 *   disabled  bool
 *   onFiles(File[])
 *   hint      the supported-formats line
 */
export default function DropZone({ accept, multiple = false, disabled = false, onFiles, hint }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  // dragenter/dragleave fire for every child element — count depth instead of
  // toggling, or the highlight flickers as the pointer crosses the icon.
  const depth = useRef(0);

  const emit = useCallback((list) => {
    const files = Array.from(list || []);
    if (files.length && onFiles) onFiles(multiple ? files : files.slice(0, 1));
  }, [multiple, onFiles]);

  const onDrop = (e) => {
    e.preventDefault();
    depth.current = 0;
    setOver(false);
    if (disabled) return;
    emit(e.dataTransfer?.files);
  };

  return (
    <div
      className={`ep-drop${over ? ' ep-over' : ''}${disabled ? ' ep-disabled' : ''}`}
      onDragEnter={(e) => { e.preventDefault(); if (disabled) return; depth.current += 1; setOver(true); }}
      onDragLeave={(e) => { e.preventDefault(); depth.current -= 1; if (depth.current <= 0) { depth.current = 0; setOver(false); } }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
    >
      <UploadCloud size={26} className="ep-drop-icon" />
      <div className="ep-drop-title">
        <button type="button" onClick={() => !disabled && inputRef.current?.click()} disabled={disabled}>
          Choose {multiple ? 'files' : 'a file'}
        </button>
        {' '}or drag {multiple ? 'them' : 'it'} here
      </div>
      {hint ? <div className="ep-drop-hint">{hint}</div> : null}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={(e) => { emit(e.target.files); e.target.value = ''; }}
        style={{ display: 'none' }}
      />
    </div>
  );
}
